import * as Icons from '@ant-design/icons';
import axios from 'axios';
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  List,
  Segmented,
  Skeleton,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
  message
} from 'antd';
import { IAcademicCalendar } from 'interfaces/academic_calendar';
import { IClassroom } from 'interfaces/classrooms';
import { formatDate } from 'services/date';
import { useCallback, useEffect, useState } from 'react';
import { useCreate, useOne } from '@refinedev/core';

interface ClassroomCalendarProps {
  classroom: IClassroom;
}

interface Asset {
  url: string;
  upload_url: string;
  download_url: string;
}

export const ClassroomCalendar = ({ classroom }: ClassroomCalendarProps) => {
  const [academicCalendar, setAcademicCalendar] =
    useState<IAcademicCalendar | null>(null);
  const [file, setFile] = useState<UploadFile | null>();
  const [newUpload, setNewUpload] = useState(false);
  const [calendarMode, setCalendarMode] = useState<'upload' | 'url'>('upload');
  const [calendarUrl, setCalendarUrl] = useState('');
  const { mutate } = useCreate<Asset>();

  const { data, isLoading } = useOne({
    resource: 'academic/calendars',
    id: classroom.id,
    errorNotification: () => ({
      message: 'Erro ao buscar calendário acadêmico',
      type: 'error'
    })
  });

  useEffect(() => {
    if (data?.data?.id) {
      setAcademicCalendar(data?.data as IAcademicCalendar);
      setNewUpload(false);
    } else {
      setAcademicCalendar(null);
      setNewUpload(true);
    }
  }, [classroom.id, data?.data]);

  const { mutateAsync: upsertAcademicCalendar } =
    useCreate<IAcademicCalendar>();

  const isExternalUrl = useCallback((value?: string | null) => {
    if (!value) {
      return false;
    }

    return /^https?:\/\//i.test(value);
  }, []);

  const handleUpsertAcademicCalendar = useCallback(
    async (url: string | null) => {
      const { data } = await upsertAcademicCalendar({
        resource: 'academic/calendars',
        values: {
          url,
          classroom_id: classroom.id
        }
      });

      setAcademicCalendar(data);
      setNewUpload(false);
    },
    [classroom.id, upsertAcademicCalendar]
  );

  const onDeleteAcademicCalendar = useCallback(() => {
    setCalendarUrl('');
    handleUpsertAcademicCalendar(null);
  }, [handleUpsertAcademicCalendar]);

  const onRestoreAcademicCalendar = useCallback(
    (url: string) => {
      handleUpsertAcademicCalendar(url);
    },
    [handleUpsertAcademicCalendar]
  );

  const createContent = () =>
    new Promise<Asset>((resolve, reject) => {
      mutate(
        {
          resource: 'content_files',
          values: {
            type: 'academic_calendar',
            extension: file?.name.split('.').pop()
          },
          successNotification: () => ({
            type: 'success',
            description: 'Sucesso!',
            message: 'Upload do calendário realizado com sucesso'
          }),
          errorNotification: () => ({
            type: 'error',
            description: 'Ops!',
            message:
              'Não foi possível realizar o upload do calendário. Tente novamente.'
          })
        },
        {
          onSuccess: ({ data }) => resolve(data),
          onError: error => reject(error)
        }
      );
    });

  const upload = async () => {
    try {
      const { url, upload_url } = await createContent();
      await axios.put(upload_url, file);
      await handleUpsertAcademicCalendar(url);

      message.success(`${file?.name} upload realizado com sucesso.`);
    } catch {
      message.error(`${file?.name} falha no upload.`);
    }
  };

  const props: UploadProps = {
    name: 'calendar_file',
    accept: 'image/jpeg, image/png, image/jpg, application/pdf',
    fileList: file ? [file] : [],
    multiple: false,
    onRemove: () => setFile(null),
    beforeUpload: newFile => {
      setFile(newFile);
      return false;
    },
    onChange: info => {
      if (info.file.status === 'removed') {
        return;
      }
      upload();
    }
  };

  const handleNewUpload = useCallback(() => {
    setFile(null);
    setCalendarUrl('');
    setCalendarMode('upload');
    setNewUpload(true);
  }, []);

  const onSaveExternalUrl = useCallback(async () => {
    const normalizedUrl = calendarUrl.trim();

    if (!normalizedUrl || !isExternalUrl(normalizedUrl)) {
      message.error('Informe uma URL válida iniciando com http:// ou https://');
      return;
    }

    try {
      await handleUpsertAcademicCalendar(normalizedUrl);
      message.success('URL do calendário salva com sucesso.');
    } catch {
      message.error('Não foi possível salvar a URL do calendário.');
    }
  }, [calendarUrl, handleUpsertAcademicCalendar, isExternalUrl]);

  if (isLoading) {
    return <Skeleton active />;
  }

  return (
    <>
      <Flex justify="space-between" align="top">
        <div className="">
          <Typography.Title level={4}>Calendário da turma</Typography.Title>
          <Typography.Paragraph type="secondary">
            Faça upload de uma imagem ou arquivo do calendário de atividades
            desta turma, ou adicione uma URL externa.
            <br />
            Os formatos aceitos para upload são: JPG, PNG ou PDF.
            <br />
            Os alunos poderão visualizar este calendário para acompanhar as
            atividades e prazos.
          </Typography.Paragraph>
        </div>

        {!!academicCalendar?.id && !newUpload && (
          <Button
            type="primary"
            icon={<Icons.UploadOutlined />}
            onClick={handleNewUpload}
          >
            Novo upload
          </Button>
        )}
      </Flex>

      {newUpload && (
        <>
          <Segmented
            className="mb-4"
            value={calendarMode}
            onChange={value => setCalendarMode(value as 'upload' | 'url')}
            options={[
              { label: 'Upload de arquivo', value: 'upload' },
              { label: 'Adicionar URL', value: 'url' }
            ]}
          />

          {calendarMode === 'upload' ? (
            <Upload.Dragger {...props}>
              <Icons.UploadOutlined className="text-neutral-500 text-4xl mb-6" />
              <p className="ant-upload-text">
                Clique para fazer upload ou arraste e solte o arquivo
              </p>
              <p className="ant-upload-hint">
                Suporte para imagens PNG ou JPG e arquivos PDF
              </p>
              <Button type="default">Selecionar arquivo</Button>
            </Upload.Dragger>
          ) : (
            <Card className="border-neutral-200">
              <Flex vertical gap={12}>
                <Typography.Text>
                  Informe uma URL externa do calendário (http:// ou https://).
                </Typography.Text>
                <Input
                  placeholder="https://exemplo.com/calendario"
                  value={calendarUrl}
                  onChange={event => setCalendarUrl(event.target.value)}
                />
                <Flex justify="flex-end">
                  <Button type="primary" onClick={onSaveExternalUrl}>
                    Salvar URL
                  </Button>
                </Flex>
              </Flex>
            </Card>
          )}
        </>
      )}

      {!!academicCalendar?.preview_url && !newUpload && (
        <Card className=" border-neutral-200">
          <Flex justify="space-between" align="center">
            <Typography.Title level={5}>Calendário atual</Typography.Title>
            <Button
              type="default"
              danger
              icon={<Icons.DeleteOutlined />}
              onClick={onDeleteAcademicCalendar}
            >
              Excluir
            </Button>
          </Flex>

          <Alert
            icon={<Icons.ExclamationCircleOutlined />}
            showIcon
            message={`Calendário publicado em ${formatDate(
              academicCalendar.created_at,
              'lll'
            )}`}
            type="info"
            className="my-6"
          />

          <div className="w-full flex justify-center items-center">
            {isExternalUrl(academicCalendar?.url) ? (
              <iframe
                src={academicCalendar?.preview_url}
                width="100%"
                height="600"
                style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
                title="Calendário externo"
              />
            ) : academicCalendar?.url.includes('pdf') ? (
              <iframe
                src={academicCalendar?.preview_url}
                width="100%"
                height="600"
                style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
                title="Calendário PDF"
              />
            ) : (
              <img
                src={academicCalendar?.preview_url}
                alt="Calendário"
                className="w-full h-auto"
              />
            )}
          </div>

          <Flex justify="flex-end" className="mt-4">
            <Button
              type="link"
              href={academicCalendar?.download_url || academicCalendar?.preview_url}
              target="_blank"
            >
              Abrir em nova aba
            </Button>
          </Flex>
        </Card>
      )}

      {!!academicCalendar && academicCalendar?.versions?.length > 0 && (
        <List
          itemLayout="horizontal"
          bordered
          className="border-neutral-200 mt-4"
          dataSource={academicCalendar?.versions || []}
          header={
            <Typography.Title level={5} className="!pt-4 !mb-0">
              Histórico de versões
            </Typography.Title>
          }
          renderItem={({ created_at, previous_url }) => (
            <List.Item>
              <Flex
                align="center"
                justify="space-between"
                gap={10}
                className="w-full"
              >
                <Flex align="center" gap={10} flex={1}>
                  <Icons.ClockCircleOutlined className="text-xl text-neutral-500" />
                  <Flex vertical gap={0}>
                    <Typography.Text className="!mb-0">
                      {previous_url || 'Calendário excluído'}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {formatDate(created_at, 'lll')}
                    </Typography.Text>
                  </Flex>
                </Flex>
                <Button
                  type="default"
                  size="small"
                  onClick={() => onRestoreAcademicCalendar(previous_url)}
                >
                  Restaurar
                </Button>
              </Flex>
            </List.Item>
          )}
        />
      )}
    </>
  );
};
